import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { InquiryPublicDto, InquiryStatus, OfferStatus } from '@paladin-monorepo/common';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { isInquiryOfferDeadlinePassed } from '@/utils/isInquiryOfferDeadlinePassed';
import { InquiryOfferDeadlineDisplay } from '@/components/inquiries/InquiryOfferDeadlineDisplay';
import { Clock, Eye, FileText } from 'lucide-react';
import { ProfileCompletenessCard } from '@/components/profile/ProfileCompletenessCard';
import { cn } from '@/lib/utils';
import {
  dashboardWidgetCardClassName,
  dashboardWidgetTitleClassName,
} from '@/components/dashboard/dashboard-widget-styles';

type SupplierDashboardSectionProps = {
  inquiries: InquiryPublicDto[];
};

export const SupplierDashboardSection = ({ inquiries }: SupplierDashboardSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const inquiriesNeedingOffer = useMemo(
    () =>
      inquiries.filter((inquiry) => {
        if (inquiry.status !== InquiryStatus.Published) return false;
        if (isInquiryOfferDeadlinePassed(inquiry.deadline)) return false;
        return !inquiry.userOffer || inquiry.userOffer.status === OfferStatus.Draft;
      }),
    [inquiries]
  );

  const inquiriesWithSubmittedOffer = useMemo(
    () => inquiries.filter((inquiry) => inquiry.userOffer?.status === OfferStatus.Submitted),
    [inquiries]
  );

  return (
    <div className="space-y-5">
      <ProfileCompletenessCard enabled />
      <div className="grid gap-5 lg:grid-cols-2">
        <Card className={dashboardWidgetCardClassName}>
          <CardHeader className="border-b border-border-subtle px-5 py-5">
            <CardTitle className={cn('flex items-center gap-2', dashboardWidgetTitleClassName)}>
              <FileText className="h-4 w-4" />
              {t('dashboard.supplier.needsOffer.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-5">
            {inquiriesNeedingOffer.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t('dashboard.supplier.needsOffer.empty')}
              </p>
            ) : (
              <div className="space-y-3">
                {inquiriesNeedingOffer.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    className="flex items-center justify-between rounded-sm border border-border-subtle p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/inquiries/${inquiry.id}`}
                        className="text-body font-medium text-brand-secondary hover:underline line-clamp-1"
                      >
                        {inquiry.title}
                      </Link>
                      <InquiryOfferDeadlineDisplay
                        deadline={inquiry.deadline}
                        showLabel
                        labelKey="dashboard.supplier.deadline"
                        className="mt-1 text-sm"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(
                          inquiry.userOffer
                            ? `/offers/${inquiry.userOffer.id}`
                            : `/inquiries/${inquiry.id}/offers/new`
                        )
                      }
                    >
                      <FileText className="mr-1 h-4 w-4" />
                      {inquiry.userOffer
                        ? t('dashboard.supplier.editOffer')
                        : t('dashboard.supplier.createOffer')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={dashboardWidgetCardClassName}>
          <CardHeader className="border-b border-border-subtle px-5 py-5">
            <CardTitle className={cn('flex items-center gap-2', dashboardWidgetTitleClassName)}>
              <Clock className="h-4 w-4" />
              {t('dashboard.supplier.submitted.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-5">
            {inquiriesWithSubmittedOffer.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t('dashboard.supplier.submitted.empty')}
              </p>
            ) : (
              <div className="space-y-3">
                {inquiriesWithSubmittedOffer.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    className="flex items-center justify-between rounded-sm border border-border-subtle p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/inquiries/${inquiry.id}`}
                        className="text-body font-medium text-brand-secondary hover:underline line-clamp-1"
                      >
                        {inquiry.title}
                      </Link>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {t('dashboard.supplier.waitingForDecision')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => inquiry.userOffer && navigate(`/offers/${inquiry.userOffer.id}`)}
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      {t('dashboard.supplier.viewOffer')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
